import type { Cell, Row } from '@tanstack/react-table';

import type { BOMRow, ExtractedTableJson } from '@frontend/api';

import { SpreadsheetSelectionCell } from 'modules/quotes/quoteItemComponents/bomBuilder/useSpreadsheetSelection';

/**
 * A table can be retried only if it has a page position to resubmit (spreadsheet-derived
 * tables have none) and didn't itself come from a manual crop/retry - retrying a manual
 * selection doesn't make sense. `printPageId` alone doesn't guarantee the coordinate
 * fields are populated, so they're checked explicitly rather than assumed.
 */
export const canRetryExtractedTable = (table: ExtractedTableJson): boolean =>
  table.printPageId != null &&
  !table.isFromManualCrop &&
  table.x1 != null &&
  table.y1 != null &&
  table.width != null &&
  table.height != null;

export const getCellFromSpreadsheetCell = (
  rows: Row<any>[],
  spreadsheetCell?: SpreadsheetSelectionCell,
) =>
  spreadsheetCell
    ? (rows[spreadsheetCell.row].getAllCells()[spreadsheetCell.column] as Cell<
        any,
        any
      >)
    : undefined;

export function countBOMRows(bomRows: BOMRow[]): number {
  // Base Case: If no BOM rows, return 0
  if (bomRows.length === 0) return 0;

  // Induction Step DFS, break out first row and accumulate the rest
  const [firstRow, ...remainingRows] = bomRows;
  return (
    // Count the current row
    1 +
    // If we have children, count them recursively
    (firstRow.children ? countBOMRows(firstRow.children) : 0) +
    // Recurse on the remaining rows
    countBOMRows(remainingRows)
  );
}

/**
 * Using the selected cell, the extent cell, the number of rows and number of columns in a table, this function
 * returns a human readable string that describes the selected range, used mainly for reporting user behavior
 * to a logging service e.g. FullStory
 */
export function getSemanticSelectionRange({
  selectedCell,
  extentCell,
  numRows,
  numColumns,
}: {
  selectedCell: SpreadsheetSelectionCell | null;
  extentCell: SpreadsheetSelectionCell | null;
  numRows: number;
  numColumns: number;
}): string {
  // Handle cases where selectedCell or extentCell are null
  // NB: This should never happen, but we're adding this check to be safe
  if (!selectedCell) {
    return 'invalid range';
  }

  // If we don't have a selected extent, then we've only selected a single cell
  if (!extentCell) {
    return 'single cell';
  }

  // Derive the total number of selected cells
  const startRow = Math.min(selectedCell.row, extentCell.row);
  const endRow = Math.max(selectedCell.row, extentCell.row);
  const startColumn = Math.min(selectedCell.column, extentCell.column);
  const endColumn = Math.max(selectedCell.column, extentCell.column);
  const rowCount = endRow - startRow + 1;
  const columnCount = endColumn - startColumn + 1;
  const totalSelectedCells = rowCount * columnCount;

  // If the range of cells equals the total number of cells, then we've selected all cells
  if (totalSelectedCells === numRows * numColumns) {
    return 'all';
  }

  // Calculate the difference between the selected cell and the extent cell for both the row and column
  const rowDifference = Math.abs(extentCell.row - selectedCell.row) + 1;
  const columnDifference =
    Math.abs(extentCell.column - selectedCell.column) + 1;

  // Using the difference between the selected cell and the extent cell, we can determine what number of rows/columns we've selected
  if (columnDifference === numColumns && rowDifference === 1) {
    return 'single row';
  } else if (columnDifference === numColumns && rowDifference > 1) {
    return `${rowDifference} rows`;
  } else if (rowDifference === numRows && columnDifference === 1) {
    return 'single column';
  } else if (rowDifference === numRows && columnDifference > 1) {
    return `${columnDifference} columns`;
  }

  // Finally, if it's just a bunch of cells, we've just selected a range
  return 'range';
}

export function separateBomsAndTables(extractedTables: ExtractedTableJson[]): {
  boms: ExtractedTableJson[];
  tables: ExtractedTableJson[];
} {
  const boms = [];
  const tables = [];
  for (const table of extractedTables.sort((a, b) => a.id - b.id)) {
    if (
      table.hasBom &&
      !table.hasInconsistentDescriptionIdentifier &&
      !table.hasInconsistentPartNumberIdentifier &&
      !table.hasInvalidQuantity &&
      !table.hasMissingQuantity
    ) {
      boms.push(table);
    } else {
      tables.push(table);
    }
  }

  return { boms, tables };
}
